import { supabase } from '@/lib/integrations/supabase'
import { validateCapability } from '@/lib/permissions/permissions'
import type { Role } from '@/lib/permissions/types'
import type {
  DeleteOrInactivateResult,
  InactivatePartyResult,
  Party,
  PartyDocument,
  PartyDocumentUploadResult,
  PartyInput,
  PartyStats,
  PartyWithStats,
} from './contactsTypes'

interface PartyFinancialEntry {
  id: string
  unit_id: string
  party_id: string | null
  entry_type: 'receivable' | 'payable'
  amount: number
  due_date: string
  status: 'open' | 'paid' | 'canceled'
  paid_at: string | null
}

export async function upsertParty(unitId: string, name: string, whatsapp?: string): Promise<string> {
  const trimmedName = name.trim()

  const { data: existing } = await supabase
    .from('parties')
    .select('id')
    .eq('unit_id', unitId)
    .ilike('name', trimmedName)
    .maybeSingle()

  if (existing) {
    if (whatsapp !== undefined) {
      await supabase
        .from('parties')
        .update({ whatsapp: whatsapp.trim() || null })
        .eq('id', existing.id)
    }
    return existing.id
  }

  const { data: created, error } = await supabase
    .from('parties')
    .insert({ unit_id: unitId, name: trimmedName, whatsapp: whatsapp?.trim() || null })
    .select('id')
    .single()
  if (error) throw error
  return (created as { id: string }).id
}

export async function listParties(unitId: string, includeInactive = false): Promise<Party[]> {
  let query = supabase
    .from('parties')
    .select('*')
    .eq('unit_id', unitId)
    .order('name', { ascending: true })

  if (!includeInactive) query = query.eq('is_active', true)

  const { data, error } = await query
  if (error) throw error
  return (data as Party[]) || []
}

export async function getPartyById(id: string): Promise<Party | null> {
  const { data, error } = await supabase
    .from('parties')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data as Party | null
}

export async function createParty(unitId: string, input: PartyInput, createdBy: string): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .insert({
      unit_id: unitId,
      name: input.name.trim(),
      whatsapp: input.whatsapp?.trim() || null,
      pix_key: input.pix_key?.trim() || null,
      notes: input.notes?.trim() || null,
      created_by: createdBy,
    })
    .select()
    .single()
  if (error) throw error
  return data as Party
}

export async function updateParty(id: string, input: PartyInput): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update({
      name: input.name.trim(),
      whatsapp: input.whatsapp?.trim() || null,
      pix_key: input.pix_key?.trim() || null,
      notes: input.notes?.trim() || null,
    })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Party
}

export async function deleteParty(id: string): Promise<void> {
  const { error } = await supabase.from('parties').delete().eq('id', id)
  if (error) throw error
}

export async function inactivateParties(unitId: string, partyIds: string[]): Promise<InactivatePartyResult[]> {
  if (partyIds.length === 0) return []

  const results: InactivatePartyResult[] = []
  const { data: parties, error: partiesError } = await supabase
    .from('parties')
    .select('id, name, is_active')
    .in('id', partyIds)
  if (partiesError) throw partiesError

  const entries = await listFinancialEntriesForParties(unitId, partyIds)
  const entriesByParty = groupEntriesByParty(entries)

  for (const party of parties || []) {
    const partyEntries = entriesByParty.get(party.id) || []
    const openEntries = partyEntries.filter(e => e.status === 'open')
    const openReceivable = openEntries.filter(e => e.entry_type === 'receivable')
    const openPayable = openEntries.filter(e => e.entry_type === 'payable')

    const result: InactivatePartyResult = {
      success: false,
      partyId: party.id,
      partyName: party.name,
      wasInactivated: false,
      hasOpenEntries: openEntries.length > 0,
      openReceivableCount: openReceivable.length,
      openReceivableTotal: openReceivable.reduce((s, e) => s + e.amount, 0),
      openPayableCount: openPayable.length,
      openPayableTotal: openPayable.reduce((s, e) => s + e.amount, 0),
    }

    if (!party.is_active) {
      result.success = true
      results.push(result)
      continue
    }

    if (openEntries.length > 0) {
      result.errorMessage = `${openEntries.length} open entries prevent inactivation`
      results.push(result)
      continue
    }

    try {
      const { error } = await supabase.from('parties').update({ is_active: false }).eq('id', party.id)
      if (error) throw error
      result.success = true
      result.wasInactivated = true
    } catch (err) {
      result.errorMessage = err instanceof Error ? err.message : 'Unknown error'
    }

    results.push(result)
  }

  return results
}

export async function deleteOrInactivateParties(unitId: string, partyIds: string[]): Promise<DeleteOrInactivateResult> {
  if (partyIds.length === 0) return { successful: [], blocked: [] }

  const successful: DeleteOrInactivateResult['successful'] = []
  const blocked: DeleteOrInactivateResult['blocked'] = []

  const { data: parties, error: partiesError } = await supabase
    .from('parties')
    .select('id, name')
    .in('id', partyIds)
  if (partiesError) throw partiesError

  const entries = await listFinancialEntriesForParties(unitId, partyIds)
  const partyStats = new Map<string, { openTotal: number }>()

  for (const entry of entries) {
    if (!entry.party_id || entry.status !== 'open') continue
    const stats = partyStats.get(entry.party_id) || { openTotal: 0 }
    stats.openTotal += entry.amount
    partyStats.set(entry.party_id, stats)
  }

  for (const party of parties || []) {
    const stats = partyStats.get(party.id) || { openTotal: 0 }
    try {
      if (stats.openTotal > 0) {
        const { error } = await supabase.from('parties').update({ is_active: false }).eq('id', party.id)
        if (error) throw error
        successful.push({ id: party.id, name: party.name, action: 'inactivated' })
      } else {
        const { error } = await supabase.from('parties').delete().eq('id', party.id)
        if (error) throw error
        successful.push({ id: party.id, name: party.name, action: 'deleted' })
      }
    } catch {
      blocked.push({
        id: party.id,
        name: party.name,
        reason: stats.openTotal > 0 ? 'Erro ao inativar' : 'Erro ao deletar',
        open_total: stats.openTotal,
      })
    }
  }

  return { successful, blocked }
}

export async function reactivateParty(id: string): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update({ is_active: true })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Party
}

export async function mergeParty(sourcePartyId: string, targetParty: Party, userRole?: Role): Promise<void> {
  if (userRole) await validateCapability(userRole, 'canEditEntries')

  const { error } = await supabase.rpc('merge_party', {
    p_source_id: sourcePartyId,
    p_target_id: targetParty.id,
  })

  if (error) throw error
}

export async function listPartiesWithStats(unitId: string, includeInactive = false): Promise<PartyWithStats[]> {
  const [parties, entries] = await Promise.all([
    listParties(unitId, includeInactive),
    listFinancialEntriesForStats(unitId),
  ])

  const byParty = groupEntriesByParty(entries)

  return parties.map(party => ({
    ...party,
    ...computePartyStats(byParty.get(party.id) ?? []),
  }))
}

function computePartyStats(entries: PartyFinancialEntry[]): PartyStats {
  const today = new Date().toISOString().split('T')[0]
  const paidEntries = entries.filter(e => e.status === 'paid')
  const openEntries = entries.filter(e => e.status === 'open')
  const overdueEntries = openEntries.filter(e => e.due_date < today)

  const sevenDaysLater = new Date()
  sevenDaysLater.setDate(sevenDaysLater.getDate() + 7)
  const sevenDaysLaterStr = sevenDaysLater.toISOString().split('T')[0]

  const dueIn7DaysEntries = openEntries.filter(e => e.due_date >= today && e.due_date <= sevenDaysLaterStr)
  const dueIn7DaysReceivable = dueIn7DaysEntries.filter(e => e.entry_type === 'receivable')
  const dueIn7DaysPayable = dueIn7DaysEntries.filter(e => e.entry_type === 'payable')
  const overdueReceivable = overdueEntries.filter(e => e.entry_type === 'receivable')
  const overduePayable = overdueEntries.filter(e => e.entry_type === 'payable')
  const receivableEntries = entries.filter(e => e.entry_type === 'receivable' && e.status !== 'canceled')
  const payableEntries = entries.filter(e => e.entry_type === 'payable' && e.status !== 'canceled')

  let score_pct: number | null = null
  if (paidEntries.length > 0) {
    const onTime = paidEntries.filter(e => !!e.paid_at && e.paid_at.split('T')[0] <= e.due_date)
    score_pct = Math.round((onTime.length / paidEntries.length) * 100)
  }

  const paidDates = paidEntries.filter(e => e.paid_at).map(e => e.paid_at!).sort()

  return {
    score_pct,
    first_paid_at: paidDates[0]?.split('T')[0] ?? null,
    last_paid_at: paidDates[paidDates.length - 1]?.split('T')[0] ?? null,
    overdue_count: overdueEntries.length,
    overdue_total: overdueEntries.reduce((s, e) => s + e.amount, 0),
    overdue_receivable_count: overdueReceivable.length,
    overdue_receivable_total: overdueReceivable.reduce((s, e) => s + e.amount, 0),
    overdue_payable_count: overduePayable.length,
    overdue_payable_total: overduePayable.reduce((s, e) => s + e.amount, 0),
    receivable_count: receivableEntries.length,
    receivable_total: receivableEntries.reduce((s, e) => s + e.amount, 0),
    payable_count: payableEntries.length,
    payable_total: payableEntries.reduce((s, e) => s + e.amount, 0),
    due_in_7_days_count: dueIn7DaysEntries.length,
    due_in_7_days_total: dueIn7DaysEntries.reduce((s, e) => s + e.amount, 0),
    due_in_7_days_receivable_count: dueIn7DaysReceivable.length,
    due_in_7_days_receivable_total: dueIn7DaysReceivable.reduce((s, e) => s + e.amount, 0),
    due_in_7_days_payable_count: dueIn7DaysPayable.length,
    due_in_7_days_payable_total: dueIn7DaysPayable.reduce((s, e) => s + e.amount, 0),
    open_count: openEntries.length,
    open_total: openEntries.reduce((s, e) => s + e.amount, 0),
  }
}

async function listFinancialEntriesForStats(unitId: string): Promise<PartyFinancialEntry[]> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('id, unit_id, party_id, entry_type, amount, due_date, status, paid_at')
    .eq('unit_id', unitId)
  if (error) throw error
  return (data as PartyFinancialEntry[]) || []
}

async function listFinancialEntriesForParties(unitId: string, partyIds: string[]): Promise<PartyFinancialEntry[]> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('id, unit_id, party_id, entry_type, amount, due_date, status, paid_at')
    .eq('unit_id', unitId)
    .in('party_id', partyIds)
  if (error) throw error
  return (data as PartyFinancialEntry[]) || []
}

function groupEntriesByParty(entries: PartyFinancialEntry[]) {
  const byParty = new Map<string, PartyFinancialEntry[]>()
  for (const entry of entries) {
    if (!entry.party_id) continue
    if (!byParty.has(entry.party_id)) byParty.set(entry.party_id, [])
    byParty.get(entry.party_id)!.push(entry)
  }
  return byParty
}

const PARTY_DOCUMENTS_BUCKET = 'party-documents'
const PARTY_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024
const PARTY_DOCUMENT_ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export async function uploadPartyDocument(
  unitId: string,
  partyId: string,
  file: File,
  documentName?: string,
): Promise<PartyDocumentUploadResult> {
  if (file.size > PARTY_DOCUMENT_MAX_BYTES) throw new Error('O arquivo excede o limite de 10 MB.')
  if (!PARTY_DOCUMENT_ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Use PDF, imagem, Word ou Excel.')
  }

  const timestamp = Date.now()
  const ext = file.name.split('.').pop() ?? 'bin'
  const path = `${unitId}/${partyId}/${timestamp}-document.${ext}`

  const { error: uploadError } = await supabase.storage.from(PARTY_DOCUMENTS_BUCKET).upload(path, file)
  if (uploadError) throw uploadError

  const { data: { user } } = await supabase.auth.getUser()
  const { data: docRecord, error: dbError } = await supabase
    .from('party_documents')
    .insert({
      party_id: partyId,
      unit_id: unitId,
      document_url: path,
      document_name: documentName || file.name,
      created_by: user?.id,
    })
    .select()
    .single()

  if (dbError) {
    await supabase.storage.from(PARTY_DOCUMENTS_BUCKET).remove([path])
    throw new Error(`Erro ao salvar documento: ${dbError.message}`)
  }

  const { data: signedData } = await supabase.storage
    .from(PARTY_DOCUMENTS_BUCKET)
    .createSignedUrl(path, 3600)

  return {
    id: docRecord.id,
    document_name: docRecord.document_name,
    document_url: path,
    url: signedData?.signedUrl ?? '',
  }
}

export async function getPartyDocuments(partyId: string): Promise<PartyDocument[]> {
  const { data, error } = await supabase
    .from('party_documents')
    .select('*')
    .eq('party_id', partyId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data || []
}

export async function getPartyDocumentUrl(documentPath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(PARTY_DOCUMENTS_BUCKET)
    .createSignedUrl(documentPath, 3600)
  if (error) throw error
  return data.signedUrl
}

export async function updatePartyDocumentName(documentId: string, documentName: string): Promise<PartyDocument> {
  const { data, error } = await supabase
    .from('party_documents')
    .update({ document_name: documentName })
    .eq('id', documentId)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deletePartyDocument(documentId: string, documentPath: string): Promise<void> {
  const { error: dbError } = await supabase.from('party_documents').delete().eq('id', documentId)
  if (dbError) throw dbError

  const { error: storageError } = await supabase.storage.from(PARTY_DOCUMENTS_BUCKET).remove([documentPath])
  if (storageError) console.warn('Failed to delete document from storage:', storageError)
}
